import { Notification } from '@progress/kendo-react-notification';
import React from 'react';
import { useUiAdminConfigsContext } from '../../context/uiAdminConfigs/UiAdminConfigsContext';
import { GearMessageBarState } from '../../Model/GearMessageBarState';
import { RoutePath } from '../../global/enums/RoutePath';
import { GearMaintenancePageState } from '../../Model/GearMaintenancePageState';
import { GearMessageBarType } from '../../Model/enums/GearMessageBarType';
import { Button } from '@progress/kendo-react-buttons';
import { SvgIcon } from '@progress/kendo-react-common';
import { chevronUpIcon, chevronDownIcon } from '@progress/kendo-svg-icons';
import { IdNamePair } from '../../Model/IdNamePair';
import { useGlobalizationContext } from '../../context/globalization/GlobalizationContext';
import { useTranslation } from '../../context/globalization/useTranslation';
import { StorageHelper } from '../../helpers/StorageHelper';

type MessageStyleUnionType = 'warning' | 'error' | 'success' | 'info' | 'none';

const getKendoMessageType = (messageBarType: IdNamePair): { style: MessageStyleUnionType } => {
  switch (messageBarType.id) {
    case GearMessageBarType.WARNING:
      return { style: 'warning' };
    case GearMessageBarType.ERROR:
    case GearMessageBarType.BLOCKED:
      return { style: 'error' };
    case GearMessageBarType.SUCCESS:
      return { style: 'success' };
    case GearMessageBarType.SEVERE_WARNING:
      return { style: 'warning' };
    default:
      return { style: 'info' };
  }
};

const generateMessageKey = (message: GearMessageBarState): string => {
  return `messageBar_${message.messageContent?.slice(0, 50)}_${message.messageBarType}`;
};

export const GearMessageBar: React.FC = () => {
  const { uiAdminConfigState, uiAdminConfigPreviewState } = useUiAdminConfigsContext();
  const { selectedLocale } = useGlobalizationContext();
  const { translate } = useTranslation(selectedLocale);

  const [isMultiline, setIsMultiline] = React.useState(false);
  const [dismissedMessageKey, setDismissedMessageKey] = React.useState<string | null>(null);
  const [prevPreviewMessage, setPrevPreviewMessage] = React.useState<GearMessageBarState | undefined>();

  if (uiAdminConfigPreviewState.messageBar !== prevPreviewMessage) {
    setDismissedMessageKey(null);
    setPrevPreviewMessage(uiAdminConfigPreviewState.messageBar as GearMessageBarState | undefined);
  }

  const messageBarToShow = React.useMemo(() => {
    let message: GearMessageBarState | undefined;

    if ((uiAdminConfigState.maintenancePage as GearMaintenancePageState)?.enabled) {
      message = {
        enabled: true,
        messageContent:
          'ADMIN ONLY: Maintenance page is enabled for all users. To disable it go to Settings -> Admin Center -> Maintenance Page',
        messageBarType: {
          id: GearMessageBarType.WARNING,
          name: translate('custom.settings.adminCenter.messageBar.type.warning')
        },
        truncated: false,
        hyperlinkText: 'PREVIEW',
        hyperlinkAddress: `${location.origin}/${RoutePath.MAINTENANCE}`
      };
    } else if (uiAdminConfigPreviewState.messageBar) {
      message = uiAdminConfigPreviewState.messageBar as GearMessageBarState;
    } else if (uiAdminConfigState.messageBar) {
      message = uiAdminConfigState.messageBar as GearMessageBarState;
    }

    if (!message) {
      return undefined;
    }

    const messageKey = generateMessageKey(message);

    if (dismissedMessageKey === messageKey) {
      return undefined;
    }

    if (message.isPreview) {
      return message;
    }

    const isDismissedInStorage = StorageHelper.get(messageKey) === 'dismissed';
    if (isDismissedInStorage) {
      return undefined;
    }

    return message;
  }, [
    uiAdminConfigState.maintenancePage,
    uiAdminConfigState.messageBar,
    uiAdminConfigPreviewState.messageBar,
    translate,
    dismissedMessageKey
  ]);

  const handleDismiss = (message: GearMessageBarState) => {
    const messageKey = generateMessageKey(message);

    setDismissedMessageKey(messageKey);

    if (!message.isPreview) {
      StorageHelper.set(messageKey, 'dismissed', 7);
    }
  };

  if (!messageBarToShow?.enabled) {
    return null;
  }

  return (
    <Notification
      key={generateMessageKey(messageBarToShow)}
      type={getKendoMessageType(messageBarToShow.messageBarType)}
      className="gearMessageBar"
      style={{
        width: '100%'
      }}
      closable={true}
      onClose={() => handleDismiss(messageBarToShow)}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div
          style={{
            maxHeight: !messageBarToShow.truncated ? 'none' : isMultiline ? 'none' : '20px',
            overflow: messageBarToShow.truncated ? 'hidden' : 'visible',
            whiteSpace: isMultiline ? 'pre-line' : 'normal',
            fontFamily: 'inherit',
            margin: 0
          }}
        >
          {messageBarToShow.messageContent}
        </div>

        <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '8px' }}>
          {messageBarToShow.hyperlinkAddress && (
            <div style={{ display: 'flex' }}>
              <Button
                themeColor="primary"
                fillMode={'outline'}
                onClick={() => window.open(messageBarToShow.hyperlinkAddress, '_blank')}
                style={{ height: '24px' }}
              >
                {messageBarToShow.hyperlinkText || ''}
              </Button>
            </div>
          )}
          <SvgIcon
            icon={isMultiline ? chevronUpIcon : chevronDownIcon}
            className="collapseIcon"
            onClick={() => setIsMultiline(!isMultiline)}
            style={{ cursor: 'pointer' }}
          />
        </div>
      </div>
    </Notification>
  );
};
